import React, { useEffect, useMemo, useState } from "react";
import CheckBox from "../../../../components/UI/CheckBox";
import { Button } from "../../../../components/UI/Button";
import TextInput from "../../../../components/UI/TextInput";
import Modal from "../../../../components/UI/Modal";
import { companyOrderUseCase } from "../../../../../di/container";
import type { PortfolioLinkedOrder } from "../../../../../domain/types/content";
import type { OrderEntity } from "../../../../../domain/types/orders";
import { resolveErrorMessage } from "../../../../../shared/utils/save-flow";

/** 한 번에 가져올 주문 수 */
const PAGE_SIZE = 30;

/**
 * 주문에서 화면에 내보낼 것을 뽑는다.
 *
 * **상품명과 옵션 문구만 가져온다.** 주문 응답에는 주문자명·이메일·수량·
 * 단가·총액이 함께 들어 있지만 어느 것도 읽지 않는다. 포트폴리오는 쇼핑몰
 * 방문자에게 공개되는 화면이고, 관리자 화면에서 보여준 것이 곧 "공개해도
 * 되는 것"이라는 인상을 만들기 때문에 선택 단계에서부터 뺀다.
 *
 * 문구 형식(`"종류: 값"`)은 서버·쇼핑몰과 맞추되, **폴백 동작은 의도적으로 다르다.**
 * 서버(`PortfolioLinkedOrderDto.readableOption`)는 snapshot 이 JSON 으로 보이면 값을 버리고
 * 종류만 남긴다 — 공개 페이지에 내부 스냅샷 원문을 내보내지 않기 위해서다.
 * 이 모달은 그 폴백을 따르지 않고 원본을 그대로 보여준다. 여기는 관리자가 *어느 주문인지
 * 식별하는* 화면이라, 정보를 줄이면 옵션이 다른 두 주문이 똑같이 보여 고를 수 없게 된다.
 * 공개 화면이 정보를 줄이는 건 안전하지만 선택 화면이 줄이는 건 고장이다.
 *
 * 두 화면의 출력이 달라 보인다고 한쪽으로 맞추지 말 것.
 */
const toLinkedOrder = (order: OrderEntity): PortfolioLinkedOrder => ({
  orderId: order.id,
  // 주문 요약 응답에는 상품명 필드가 따로 없어 주문명을 쓴다 (기존 화면들과 동일)
  productName: order.name ?? `주문 ${order.id}`,
  options: Array.isArray(order.options)
    ? order.options
        .map((option) =>
          typeof option === "string"
            ? option
            : [option.optionName, option.optionValue]
                .filter((part) => part && String(part).trim() !== "")
                .join(": "),
        )
        .filter((label) => label.trim() !== "")
    : [],
});

interface PortfolioOrderPickerModalProps {
  setIsModalVisible: React.Dispatch<React.SetStateAction<boolean>>;
  isModalVisible: boolean;
  companyId: number;
  /** 현재 연결된 주문 (배열 순서가 곧 노출 순서) */
  selectedOrders: PortfolioLinkedOrder[];
  onChange: (orders: PortfolioLinkedOrder[]) => void;
}

/**
 * 포트폴리오에 연결할 주문을 고르는 모달.
 *
 * 검색형 모달 구조는 SectionProductPickerModal 과 같고, 다중 선택은
 * TaxReceipt 의 체크박스 패턴을 따른다. 다만 목록이 회사 전체 주문이라
 * 서버 페이징을 쓴다 — 상품처럼 전부 받아 두고 프런트에서 거르기엔 많다.
 */
export const PortfolioOrderPickerModal = ({
  setIsModalVisible,
  isModalVisible,
  companyId,
  selectedOrders,
  onChange,
}: PortfolioOrderPickerModalProps) => {
  const [searchKeyword, setSearchKeyword] = useState("");
  const [page, setPage] = useState(0);
  const [orders, setOrders] = useState<OrderEntity[]>([]);
  const [totalPages, setTotalPages] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const selectedIds = useMemo(
    () => new Set(selectedOrders.map((order) => order.orderId)),
    [selectedOrders],
  );

  // 검색어가 바뀌면 첫 페이지부터 다시 본다
  useEffect(() => {
    setPage(0);
  }, [searchKeyword]);

  useEffect(() => {
    if (!isModalVisible) return;

    let isStale = false;
    // 타이핑마다 요청하지 않도록 잠깐 기다린다
    const timer = setTimeout(async () => {
      setIsLoading(true);
      setErrorMessage(null);

      try {
        const result = await companyOrderUseCase.getOrders(
          companyId,
          undefined,
          searchKeyword.trim() || undefined,
          page,
          PAGE_SIZE,
        );

        if (isStale) return;
        setOrders(result.items);
        setTotalPages(result.totalPages);
      } catch (error) {
        if (isStale) return;
        console.error(error);
        setOrders([]);
        setTotalPages(0);
        setErrorMessage(
          resolveErrorMessage(error, "주문 목록을 불러오지 못했습니다."),
        );
      } finally {
        if (!isStale) setIsLoading(false);
      }
    }, 300);

    return () => {
      isStale = true;
      clearTimeout(timer);
    };
  }, [isModalVisible, companyId, searchKeyword, page]);

  const toggleOrder = (order: OrderEntity) => {
    if (selectedIds.has(order.id)) {
      onChange(selectedOrders.filter((item) => item.orderId !== order.id));
      return;
    }

    // 새로 고른 주문은 맨 뒤에 붙는다 — 배열 순서가 곧 노출 순서다
    onChange([...selectedOrders, toLinkedOrder(order)]);
  };

  if (!isModalVisible) {
    return null;
  }

  return (
    <Modal setIsModalVisible={setIsModalVisible} title="">
      <div className="w-[calc(100vw-20vw)] max-w-[31.875rem]">
        <div className="flex flex-col items-start / mb-[1.0625rem]">
          <h2 className="mb-[0.75rem]">주문 연결</h2>
          <div className="text-[0.875rem] font-[500]">
            {selectedOrders.length}개 선택됨
          </div>
          <div className="text-[12px] text-[#AEAEAE] mt-[0.25rem] text-left">
            상품명과 옵션 구성만 쇼핑몰에 노출됩니다. 수량·금액과 주문자 정보는
            표시되지 않습니다.
          </div>
        </div>

        <div className="relative py-[1rem]">
          <div
            className="absolute top-0 bottom-0 left-0 right-0
              / mx-[-19px] / bg-[#F9FAFB] / outline outline-1 outline-[#E5E7EB]"
          ></div>
          <TextInput
            value={searchKeyword}
            onChange={(e) => setSearchKeyword(e.target.value)}
            className="w-full"
            placeholder="주문 검색..."
          />
        </div>

        <div
          className="h-[308px] overflow-y-scroll / flex flex-col
            / gap-[1.1rem] mt-[1.390625rem] mb-[1.390625rem]"
        >
          {orders.map((order) => {
            const isSelected = selectedIds.has(order.id);
            const linked = toLinkedOrder(order);

            return (
              <React.Fragment key={order.id}>
                <div className="flex flex-row justify-between items-center gap-3">
                  <div className="flex flex-row gap-[1rem] items-start min-w-0">
                    <button onClick={() => toggleOrder(order)} className="mt-1">
                      <CheckBox
                        className="min-w-[1rem]"
                        isEnabled={isSelected}
                      />
                    </button>

                    <div className="flex flex-col items-start justify-center min-w-0">
                      <h3 className="text-[0.9375rem] text-left">
                        {linked.productName}
                      </h3>
                      <div className="text-[#AEAEAE] text-[12px] text-left">
                        {linked.options.length > 0
                          ? linked.options.join(" · ")
                          : "옵션 없음"}
                      </div>
                    </div>
                  </div>
                  {isSelected ? (
                    <div
                      className="hidden min-w-[70px] justify-center md:flex text-[0.75rem] text-white / bg-[#4285F4]
                        / px-[0.8rem] py-[0.6rem] h-min / rounded-full
                        / items-center gap-[0.375rem]"
                    >
                      <div
                        className="bg-white / w-[0.375rem] h-[0.375rem]
                          / rounded-full"
                      />
                      선택됨
                    </div>
                  ) : null}
                </div>
                <hr />
              </React.Fragment>
            );
          })}

          {isLoading ? (
            <div className="py-[1rem] text-center text-[#AEAEAE]">
              불러오는 중입니다...
            </div>
          ) : null}

          {errorMessage ? (
            <div className="py-[1rem] text-center text-[#F24A4A] text-[13px]">
              {errorMessage}
            </div>
          ) : null}

          {!isLoading && !errorMessage && orders.length === 0 ? (
            <div className="py-[1rem] text-center text-[#AEAEAE]">
              검색 결과가 없습니다.
            </div>
          ) : null}
        </div>

        <div className="relative">
          <div
            className="absolute top-0 bottom-0 left-0 right-0
              / mx-[-19px]  / bg-[#F9FAFB] outline outline-1 outline-[#E5E7EB]
              / rounded-b-[12px]"
          ></div>
          <div
            className="relative z-50 / flex flex-row justify-between items-center
              / py-[1rem] mb-[-20px]"
          >
            <div className="flex flex-row items-center gap-[0.5rem]">
              <Button
                variant="outline"
                disabled={page === 0 || isLoading}
                onClick={() => setPage((current) => Math.max(0, current - 1))}
              >
                이전
              </Button>
              <div className="text-[0.8125rem]">
                {totalPages === 0 ? 0 : page + 1} / {totalPages}
              </div>
              <Button
                variant="outline"
                disabled={page + 1 >= totalPages || isLoading}
                onClick={() => setPage((current) => current + 1)}
              >
                다음
              </Button>
            </div>
            <div>
              <Button
                onClick={() => {
                  setIsModalVisible(false);
                }}
                className="w-[4.014375rem] h-[2.5625rem]"
              >
                완료
              </Button>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};
